import { Analytics } from '@cobliteam/analytics';
import type { Insight } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useInsights } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Textarea from '@cobliteam/ui/form/Textarea';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { subWeeks } from '@cobliteam/utils/date';
import { useEffect, useState } from 'react';
import insightsUtils from '../../../utils';

export interface InsightContentProps {
  insight: Insight;
  isFirstInsight: boolean;
  isLastInsight: boolean;
  onPreviousInsightClick: () => void;
  onNextInsightClick: () => void;
}

type FeedbackOption = 'thumbs-up' | 'thumbs-down';

const InsightContent = ({ insight, isFirstInsight, isLastInsight, onPreviousInsightClick, onNextInsightClick }: InsightContentProps) => {
  const { t } = useTranslation('insights');
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackOption | undefined>(undefined);
  const [negativeFeedbackMessage, setNegativeFeedbackMessage] = useState('');
  const [showSuccessFeedbackMessage, setShowSuccessFeedbackMessage] = useState(false);

  const { getInsightType } = insightsUtils();

  const getTranslatedCategory = (category?: string | null) => {
    if (!category) return 'Insight';
    const translationKey = `categories.${category.toLowerCase()}`;
    const translated = t(translationKey);
    // If translation doesn't exist, return the original category
    return translated !== translationKey ? translated : category;
  };

  const getCategoryIcon = (category?: string | null) => {
    if (!category) return 'lightbulb';
    const categoryLower = category.toLowerCase();
    if (categoryLower === 'safety' || categoryLower === 'security') {
      return 'shield-check';
    }
    return 'lightbulb';
  };
  const { useEvaluateInsightThumbsDown, useEvaluateInsightThumbsUp } = useInsights;
  const { mutateAsync: sendThumbsUp } = useEvaluateInsightThumbsUp({ insightId: insight.id });
  const { mutateAsync: sendThumbsDown } = useEvaluateInsightThumbsDown({ insightId: insight.id, comment: negativeFeedbackMessage });

  useEffect(() => {
    setSelectedFeedback(undefined);
    setNegativeFeedbackMessage('');
    setShowSuccessFeedbackMessage(false);
  }, [insight]);

  const onSafetyReportyClick = () => {
    const queryString = new URLSearchParams();

    // vehicle
    // temporary getting vehicleId from text while backend has invalid fields
    const vehicleRegex = /VehicleID\s*=\s*([0-9a-fA-F-]{36})/;
    const vehicleMatch = insight.insightText?.match(vehicleRegex);

    if (vehicleMatch) {
      queryString.append('vehicleIds', vehicleMatch[1]);
    } else if (insight.vehicle) {
      queryString.append('vehicleIds', insight.vehicle.id);
    }

    // driver

    // temporary getting driverId from text while backend has invalid fields
    const driverRegex = /DriverID\s*=\s*([0-9a-fA-F-]{36})/;
    const driverMatch = insight.insightText?.match(driverRegex);

    if (driverMatch) {
      queryString.append('driverIds', driverMatch[1]);
    } else if (insight.driver) {
      queryString.append('driverIds', insight.driver.id);
    }

    Analytics.track('Insight Modal Safety Report CTA Clicked');

    const reportPageView = driverMatch || insight.driver ? 'driver' : 'vehicle';

    queryString.append('endDate', insight.createdAt);
    queryString.append('startDate', subWeeks(new Date(insight.createdAt), 1).toString());

    window.open(`https://painel.cobli.co/report/safety/${reportPageView}?page=0&size=10&${queryString.toString()}`, '_blank');
  };

  const handleFeedbackClick = async (feedback: FeedbackOption) => {
    if (selectedFeedback !== undefined) return;
    setSelectedFeedback(feedback);
    if (feedback === 'thumbs-up') {
      await sendThumbsUp();
      setShowSuccessFeedbackMessage(true);
      Analytics.track('Insight Modal Feedback Clicked', { id: insight.id, type: getInsightType(insight), category: insight.category, feedbackType: 'positive' });
    }
  };

  const handleNegativeFeedbackSubmit = async () => {
    await sendThumbsDown();
    setShowSuccessFeedbackMessage(true);
    Analytics.track('Insight Modal Feedback Clicked', {
      id: insight.id,
      type: getInsightType(insight),
      category: insight.category,
      feedbackType: 'negative',
      comment: negativeFeedbackMessage,
    });
  };

  const renderPositiveFeedbackMessage = () => (
    <div className='flex flex-col gap-2 rounded-lg border border-success-4 bg-success-2 p-4'>
      <div className='flex items-center gap-2'>
        <Icon name='circle-check' color='success' className='size-5' />
        <Typography variant='text-md-semibold' className='text-success-11'>
          Obrigado pela sua resposta!
        </Typography>
      </div>
      <Typography variant='text-sm' className='text-success-10'>
        Ela será utilizada para aprimorar os próximos insights.
      </Typography>
    </div>
  );

  const renderNegativeFeedbackMessage = () => (
    <div className='flex flex-col gap-4 rounded-lg border border-gray-4 bg-white p-4'>
      <Typography variant='text-md-semibold' color='secondary'>
        Como este insight poderia ter sido mais valioso para a sua gestão?
      </Typography>
      <Textarea className='w-full' placeholder='Escrever sugestão...' value={negativeFeedbackMessage} onChange={(event) => setNegativeFeedbackMessage(event.target.value)} />
      <Button variant='contained' color='primary' size='sm' className='self-end' disabled={negativeFeedbackMessage.length === 0} onClick={handleNegativeFeedbackSubmit}>
        Enviar resposta
      </Button>
    </div>
  );

  return (
    <div className='relative flex h-full flex-col'>
      <div
        className={cn(
          'flex flex-col gap-8 overflow-y-auto p-6 pb-32',
          'scrollbar',
          'scrollbar-thumb-custom',
          'scrollbar-thin',
          'scrollbar-thumb-rounded-full scrollbar-track-rounded-full',
          'scrollbar-thumb-gray-8',
          'scrollbar-thumb-bg-clip-content',
          'scrollbar-track-gray-1'
        )}
      >
        <div className='flex flex-col gap-4'>
          <div className='flex items-center gap-3'>
            <div className='flex size-10 items-center justify-center rounded-lg bg-primary-3'>
              <Icon name={getCategoryIcon(insight.category)} color='primary' className='size-5' />
            </div>
            <div className='flex flex-col gap-1'>
              <Typography variant='text-sm-medium' color='secondary'>
                {getTranslatedCategory(insight.category)}
              </Typography>
              <Typography variant='caption-xs' color='secondary' className='text-gray-9'>
                {new Date(insight.createdAt).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </Typography>
            </div>
          </div>
        </div>

        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-3'>
            <Typography variant='heading-lg' color='secondary'>
              Insight e impacto
            </Typography>
            <div className='rounded-lg border border-gray-4 bg-gray-1 p-4'>
              <Typography variant='text-md' className='text-gray-12 leading-relaxed'>
                {insight.impact}
              </Typography>
            </div>
          </div>

          <div className='flex flex-col gap-3'>
            <Typography variant='heading-lg' color='secondary'>
              Ação Recomendada
            </Typography>
            <div className='rounded-lg border border-gray-4 bg-gray-1 p-4'>
              <Typography variant='text-md' className='text-gray-12 leading-relaxed'>
                {insight.action}
              </Typography>
            </div>
          </div>
        </div>

        <Button onClick={onSafetyReportyClick} startIcon={<Icon name='external-link' className='size-4' />} variant='outlined' color='primary' className='self-start'>
          Conferir no relatório de segurança
        </Button>

        <div className='flex flex-col gap-4 rounded-lg border border-gray-4 bg-gray-2 p-4'>
          <div className='flex items-center justify-between gap-4'>
            <Typography variant='text-md-semibold' color='secondary'>
              Este insight foi útil para você?
            </Typography>
            <div className='flex items-center gap-3'>
              <button
                type='button'
                onClick={() => handleFeedbackClick('thumbs-up')}
                className={cn(
                  'flex size-10 items-center justify-center rounded-lg transition-all duration-200',
                  selectedFeedback === 'thumbs-up' ? 'bg-success-3 text-success' : 'bg-gray-3 text-gray-9 hover:bg-gray-4'
                )}
                aria-label='Feedback positivo'
              >
                <Icon name='thumbs-up' className='size-5' />
              </button>
              <button
                type='button'
                onClick={() => handleFeedbackClick('thumbs-down')}
                className={cn(
                  'flex size-10 items-center justify-center rounded-lg transition-all duration-200',
                  selectedFeedback === 'thumbs-down' ? 'bg-danger-3 text-danger' : 'bg-gray-3 text-gray-9 hover:bg-gray-4'
                )}
                aria-label='Feedback negativo'
              >
                <Icon name='thumbs-down' className='size-5' />
              </button>
            </div>
          </div>
          {!showSuccessFeedbackMessage && selectedFeedback === 'thumbs-down' && renderNegativeFeedbackMessage()}
          {showSuccessFeedbackMessage && renderPositiveFeedbackMessage()}
        </div>
      </div>

      <div className='sticky bottom-0 z-10 flex flex-col gap-4 border-gray-4 border-t bg-white py-4'>
        <div className='flex items-center justify-between px-6'>
          {!isFirstInsight && (
            <Button startIcon={<Icon name='arrow-left' className='size-4' />} variant='ghost' color='default' onClick={onPreviousInsightClick}>
              Insight anterior
            </Button>
          )}
          {!isLastInsight && (
            <Button
              variant='contained'
              color='primary'
              endIcon={<Icon name='arrow-right' className='size-4' />}
              className={isFirstInsight ? 'ml-auto' : ''}
              onClick={onNextInsightClick}
            >
              Próximo insight
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsightContent;
